import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchOrgBranding, fetchLearnerEnrollments, type PortalLearner, type OrgBrandingInfo } from '../../api/learnPublicApi';
import { getCurrentSubdomain } from '../../utils/subdomain';

/* ── Types ── */

export interface TrainingContextValue {
  /** Magic token from URL (?t=...) — used for portal/learner endpoints */
  magicToken: string;
  /** Org domain from subdomain or ?org= query */
  orgDomain: string | null;
  /** Resolved org branding (logo, name, domain) */
  org: OrgBrandingInfo | null;
  orgLoading: boolean;
  orgNotFound: boolean;
  /** Authenticated learner (loaded via magic token) */
  learner: PortalLearner | null;
  learnerLoading: boolean;
  /** Sidebar collapse state */
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (v: boolean) => void;
  /** Mobile sidebar open state */
  mobileSidebarOpen: boolean;
  setMobileSidebarOpen: (v: boolean) => void;
  /** Current page title (set by each page) */
  pageTitle: string;
  setPageTitle: (title: string) => void;
  /** Build catalog path respecting subdomain/org query */
  catalogPath: string;
}

const TrainingCtx = createContext<TrainingContextValue | null>(null);

/* ── Hook ── */

export function useTraining(): TrainingContextValue {
  const ctx = useContext(TrainingCtx);
  if (!ctx) throw new Error('useTraining must be used inside <TrainingProvider>');
  return ctx;
}

/* ── Session storage keys ── */

const TRAINING_ORG_KEY = 'training_org_domain';

/* ── Provider ── */

export default function TrainingProvider({ children }: { children: ReactNode }) {
  const subdomain = getCurrentSubdomain();
  const [searchParams] = useSearchParams();
  const magicToken = searchParams.get('t') || '';
  const orgFromQuery = searchParams.get('org');

  // Persist org domain across navigation
  const storedOrg = (() => { try { return sessionStorage.getItem(TRAINING_ORG_KEY); } catch { return null; } })();
  const orgDomain = subdomain || orgFromQuery || storedOrg || null;

  const [org, setOrg] = useState<OrgBrandingInfo | null>(null);
  const [orgLoading, setOrgLoading] = useState(!!orgDomain);
  const [orgNotFound, setOrgNotFound] = useState(false);

  const [learner, setLearner] = useState<PortalLearner | null>(null);
  const [learnerLoading, setLearnerLoading] = useState(!!magicToken);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState('Training');

  // ── Fetch org branding ──
  useEffect(() => {
    if (!orgDomain) { setOrgLoading(false); return; }
    setOrgLoading(true);
    setOrgNotFound(false);
    fetchOrgBranding(orgDomain)
      .then((data) => {
        if (data?.company_name) {
          setOrg(data);
          try { sessionStorage.setItem(TRAINING_ORG_KEY, data.domain || orgDomain); } catch {}
        } else {
          setOrgNotFound(true);
        }
      })
      .catch(() => setOrgNotFound(true))
      .finally(() => setOrgLoading(false));
  }, [orgDomain]);

  // ── Fetch learner data when magic token is present ──
  useEffect(() => {
    if (!magicToken) { setLearnerLoading(false); return; }
    setLearnerLoading(true);
    fetchLearnerEnrollments(magicToken)
      .then((data) => {
        if (data.learner) setLearner(data.learner);
        if (data.organization && !org) setOrg(data.organization);
      })
      .catch(() => {})
      .finally(() => setLearnerLoading(false));
  }, [magicToken]);

  // ── Persist org domain from query ──
  useEffect(() => {
    if (orgFromQuery && !subdomain) {
      try { sessionStorage.setItem(TRAINING_ORG_KEY, orgFromQuery); } catch {}
    }
  }, [orgFromQuery, subdomain]);

  // ── Build catalog path ──
  const catalogPath = useMemo(() => {
    if (subdomain) return '/training/catalog';
    const domain = orgDomain || org?.domain;
    if (domain) return `/training/catalog?org=${encodeURIComponent(domain)}`;
    return '/training/catalog';
  }, [subdomain, orgDomain, org]);

  const value = useMemo<TrainingContextValue>(() => ({
    magicToken,
    orgDomain,
    org,
    orgLoading,
    orgNotFound,
    learner,
    learnerLoading,
    sidebarCollapsed,
    setSidebarCollapsed,
    mobileSidebarOpen,
    setMobileSidebarOpen,
    pageTitle,
    setPageTitle,
    catalogPath,
  }), [
    magicToken, orgDomain, org, orgLoading, orgNotFound,
    learner, learnerLoading, sidebarCollapsed, mobileSidebarOpen,
    pageTitle, catalogPath,
  ]);

  return <TrainingCtx.Provider value={value}>{children}</TrainingCtx.Provider>;
}
